import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  CheckCircle2,
  Eye,
  IndianRupee,
  Loader2,
  MoreVertical,
  RefreshCw,
  Settings2,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchGrievanceById,
  fetchGrievances,
  retryGrievanceRefund,
  updateGrievanceStatus,
  GrievanceDetailPayload,
  GrievanceListRow,
  GrievanceStatus,
} from '../../services/MasterAdmin/grievanceAdminService';
import { GrievanceViewModal } from '../../components/MasterAdmin/grievances/GrievanceViewModal';
import { GrievancePolicyLinks } from '../../components/MasterAdmin/grievances/GrievancePolicyLinks';

const formatInr = (n: number | string | null | undefined) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const STATUS_OPTIONS: { value: '' | GrievanceStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

const SERVICE_TYPE_OPTIONS = [
  { value: '', label: 'All services' },
  { value: 'AMBULANCE', label: 'Ambulance' },
  { value: 'MEDICINE', label: 'Medicine' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'LAB', label: 'Lab' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'BLOOD_BANK', label: 'Blood bank' },
  { value: 'PRODUCT', label: 'Product' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const RAISED_BY_OPTIONS = [
  { value: '', label: 'All submitters' },
  { value: 'PATIENT', label: 'Patient' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'ADMIN', label: 'Admin' },
];

const statusBadgeClass: Record<GrievanceStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  UNDER_REVIEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CLOSED: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const GrievancesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<GrievanceListRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [priority, setPriority] = useState('');
  const [raisedBy, setRaisedBy] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    serviceType: '',
    priority: '',
    raisedBy: '',
    search: '',
  });

  const [actionMenu, setActionMenu] = useState<{
    grievanceId: string;
    top: number;
    right: number;
  } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const [viewModalId, setViewModalId] = useState<string | null>(null);
  const [statusModalId, setStatusModalId] = useState<string | null>(null);
  const [detail, setDetail] = useState<GrievanceDetailPayload | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [retryingRefundId, setRetryingRefundId] = useState<string | null>(null);
  const [statusDraft, setStatusDraft] = useState<GrievanceStatus>('PENDING');

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (actionMenuRef.current?.contains(t)) return;
      const trigger = (e.target as HTMLElement | null)?.closest('[data-grievance-row-actions]');
      if (trigger?.getAttribute('data-grievance-row-actions') === actionMenu.grievanceId) return;
      close();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenu]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGrievances({
        page: pagination.page,
        limit: pagination.limit,
        status: appliedFilters.status || undefined,
        serviceType: appliedFilters.serviceType || undefined,
        priority: appliedFilters.priority || undefined,
        raisedByActorType: appliedFilters.raisedBy || undefined,
        search: appliedFilters.search.trim() || undefined,
      });
      if (!res.success) throw new Error('Failed to load grievances');
      setRows(res.data);
      setPagination(res.pagination);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load grievances');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, appliedFilters]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    const id = searchParams.get('grievanceId');
    const mode = searchParams.get('mode');
    if (id && mode === 'resolve') {
      navigate(`/grievances/${encodeURIComponent(id)}/resolve`, { replace: true });
    }
  }, [searchParams, navigate]);

  const loadDetail = useCallback(async (grievanceId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await fetchGrievanceById(grievanceId);
      if (!res.success || !res.data) throw new Error('Failed to load grievance');
      setDetail(res.data);
      setStatusDraft(res.data.grievance.status);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : 'Load failed');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openViewModal = (grievanceId: string) => {
    setViewModalId(grievanceId);
    setActionMenu(null);
  };

  const openStatusModal = (grievanceId: string) => {
    setStatusModalId(grievanceId);
    setActionMenu(null);
    void loadDetail(grievanceId);
  };

  const openResolvePage = (grievanceId: string) => {
    setActionMenu(null);
    navigate(`/grievances/${encodeURIComponent(grievanceId)}/resolve`);
  };

  const closeStatusModal = () => {
    setStatusModalId(null);
    setDetail(null);
    setDetailError(null);
  };

  const handleStatusUpdate = async () => {
    if (!statusModalId || !g || !user?.id) return;
    setSaving(true);
    setDetailError(null);
    try {
      const res = await updateGrievanceStatus(g.grievanceId, {
        status: statusDraft,
        adminId: user.id,
      });
      if (!res.success) throw new Error('Status update failed');
      closeStatusModal();
      void loadList();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Update failed');
      setDetailError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRetryRefund = async (grievance: GrievanceListRow) => {
    setActionMenu(null);
    setRetryingRefundId(grievance.grievanceId);
    try {
      const res = await retryGrievanceRefund(grievance.grievanceId);
      if (!res.success) throw new Error(res.message || res.error || 'Retry refund failed');
      toast.success(res.message || 'Refund initiated.');
      void loadList();
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { message?: string; error?: string } } })?.response
        ?.data;
      toast.error(
        data?.message || data?.error || (e instanceof Error ? e.message : 'Retry refund failed'),
      );
    } finally {
      setRetryingRefundId(null);
    }
  };

  const actionMenuRow = actionMenu ? rows.find((r) => r.grievanceId === actionMenu.grievanceId) : undefined;
  const g = statusModalId ? detail?.grievance : undefined;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <Toaster position="top-right" />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Grievances</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            User-submitted grievances across services. Review details, update status, or resolve with a refund / share
            split (ledger + Razorpay for ambulance).
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadList()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="text-sm block min-w-[140px]">
          <span className="text-gray-600 dark:text-gray-300">Status</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block min-w-[140px]">
          <span className="text-gray-600 dark:text-gray-300">Service</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            {SERVICE_TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'all-svc'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block min-w-[120px]">
          <span className="text-gray-600 dark:text-gray-300">Priority</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value || 'all-pri'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block min-w-[140px]">
          <span className="text-gray-600 dark:text-gray-300">Raised by</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={raisedBy}
            onChange={(e) => setRaisedBy(e.target.value)}
          >
            {RAISED_BY_OPTIONS.map((o) => (
              <option key={o.value || 'all-raised'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block flex-1 min-w-[200px]">
          <span className="text-gray-600 dark:text-gray-300">Search</span>
          <input
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-white"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ID, title, user, booking ref…"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setAppliedFilters({ status, serviceType, priority, raisedBy, search: searchInput });
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Apply filters
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-600 dark:text-gray-300">
              <th className="px-4 py-3 font-semibold">Created</th>
              <th className="px-4 py-3 font-semibold">Grievance</th>
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Service</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Priority</th>
              <th className="px-4 py-3 font-semibold text-right">Gross</th>
              <th className="px-4 py-3 w-[72px] text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin inline mr-2 align-middle" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr
                  key={r.grievanceId}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50"
                >
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200 whitespace-nowrap">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 dark:text-white">{r.grievanceNumber}</p>
                    <p className="max-w-[200px] truncate text-gray-500 dark:text-gray-400">{r.title}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    <p>{r.userName || r.userId}</p>
                    <p className="text-xs text-gray-500">{r.raisedByActorType}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-900 dark:text-white">{r.serviceType}</p>
                    <p className="font-mono text-xs text-gray-500 max-w-[120px] truncate">{r.serviceRefId}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass[r.status]}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.priority}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatInr(r.totalBookingAmount)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      data-grievance-row-actions={r.grievanceId}
                      aria-label="Row actions"
                      aria-expanded={actionMenu?.grievanceId === r.grievanceId}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenu((cur) =>
                          cur?.grievanceId === r.grievanceId
                            ? null
                            : {
                                grievanceId: r.grievanceId,
                                top: rect.bottom + 4,
                                right: window.innerWidth - rect.right,
                              }
                        );
                      }}
                      className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No grievances found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {actionMenu &&
        actionMenuRow &&
        createPortal(
          <div
            ref={actionMenuRef}
            className="fixed z-[300] min-w-[200px] rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-600 dark:bg-gray-800"
            style={{ top: actionMenu.top, right: actionMenu.right }}
          >
            <ul className="m-0 list-none p-0">
              <li>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                  onClick={() => openViewModal(actionMenuRow.grievanceId)}
                >
                  <Eye className="h-4 w-4 opacity-70" />
                  View
                </button>
              </li>
              <li>
                <button
                  type="button"
                  disabled={actionMenuRow.status === 'RESOLVED' || actionMenuRow.status === 'CLOSED'}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-40 dark:text-gray-100 dark:hover:bg-gray-700 dark:disabled:text-gray-500"
                  onClick={() => openResolvePage(actionMenuRow.grievanceId)}
                >
                  <CheckCircle2 className="h-4 w-4 opacity-70" />
                  Resolve
                </button>
              </li>
              {(actionMenuRow.status === 'RESOLVED' || actionMenuRow.status === 'CLOSED') &&
                Number(actionMenuRow.refundAmount) > 0 &&
                (actionMenuRow.serviceType === 'DOCTOR' ||
                  actionMenuRow.serviceType === 'AMBULANCE') && (
                  <li>
                    <button
                      type="button"
                      disabled={retryingRefundId === actionMenuRow.grievanceId}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-40 dark:text-gray-100 dark:hover:bg-gray-700"
                      onClick={() => void handleRetryRefund(actionMenuRow)}
                    >
                      {retryingRefundId === actionMenuRow.grievanceId ? (
                        <Loader2 className="h-4 w-4 animate-spin opacity-70" />
                      ) : (
                        <IndianRupee className="h-4 w-4 opacity-70" />
                      )}
                      Retry refund
                    </button>
                  </li>
                )}
              <li>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                  onClick={() => openStatusModal(actionMenuRow.grievanceId)}
                >
                  <Settings2 className="h-4 w-4 opacity-70" />
                  Update status
                </button>
              </li>
            </ul>
          </div>,
          document.body
        )}

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
              className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {viewModalId && (
        <GrievanceViewModal grievanceId={viewModalId} onClose={() => setViewModalId(null)} />
      )}

      {statusModalId && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 sm:items-center sm:p-4"
          onClick={closeStatusModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between border-b dark:border-gray-700 px-4 py-3 bg-white dark:bg-gray-800">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Update status</h2>
                {g && <p className="text-xs text-gray-500 font-mono">{g.grievanceNumber}</p>}
              </div>
              <button type="button" onClick={closeStatusModal} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {detailLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                </div>
              )}
              {detailError && (
                <p className="text-sm text-red-600 dark:text-red-400 rounded-lg bg-red-50 dark:bg-red-950/30 p-3">
                  {detailError}
                </p>
              )}

              {!detailLoading && g && (
                <>
                  <label className="block text-sm">
                    <span className="text-gray-500">Status</span>
                    <select
                      className="mt-1 w-full rounded-lg border dark:border-gray-600 dark:bg-gray-900 px-3 py-2"
                      value={statusDraft}
                      onChange={(e) => setStatusDraft(e.target.value as GrievanceStatus)}
                    >
                      {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-xs text-gray-500">
                    Use <strong>Resolve</strong> to set RESOLVED with ledger split and refund. This control is for
                    PENDING / UNDER_REVIEW / CLOSED only.
                  </p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleStatusUpdate()}
                    className="w-full rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save status'}
                  </button>
                  {statusModalId && (
                    <GrievancePolicyLinks grievanceId={statusModalId} layout="stack" className="pt-1" />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default GrievancesPage;
